import React from 'react';
import { styled } from '@mui/material/styles';
import ResourceTable, { IColumn } from '../../components/ResourceTable';
import Link from 'components/Link';
import { IFilterField } from 'models/types';
import {
  CompanyDetailedDto,
  OrganisationScoredCompaniesDto,
  PdfStyleDto,
  ReportTypeDto,
  TriggerScoringByNameDto,
} from 'models/dtoTypes';
import { GetScoredCompanies, PollCompanyScore, Score } from 'services/openapiClient';
import { GetCurrentOrganisation } from 'services/user';
import { utc2str } from 'utils/date';
import OrganisationOutOfCreditNotice from 'kosmos/components/OrganisationOutOfCreditNotice';
import Notice from '../../components/Notice';
import { CurrentUser } from 'auth';
import downloadKosmosReport from 'utils/downloadKosmosReport';
import { getKosmosReportLink, getSgReportLink } from 'utils/report';
import { pollAsync } from 'utils/polling';
import { GetOrganisationScoredCompany } from 'services/company';
import { useTranslation } from 'react-i18next';

const PREFIX = 'ScoredCompaniesPage';

const classes = {
  root: `${PREFIX}-root`,
  demo: `${PREFIX}-demo`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`&.${classes.demo}`]: {
    backgroundColor: theme.palette.background.paper,
  },
}));

export default function TfpScoredCompaniesPage(): JSX.Element {
  interface INoticeData {
    message: string;
    severity: 'error' | 'success';
  }

  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [showNotice, setShowNotice] = React.useState<boolean>(false);
  const [organisationId, setOrganisationId] = React.useState<string>('');
  const { t } = useTranslation('default');

  const filterSchema: IFilterField[] = [
    {
      label: t('name'),
      name: 'name',
      widget: 'text',
    },
    {
      label: t('external-identifier'),
      name: 'externalIdentifier',
      widget: 'text',
    },
  ];

  const handleRescoreCompany = async (
    report: OrganisationScoredCompaniesDto,
    shouldCancel?: () => boolean,
  ) => {
    const companyId = report.companyId || '';
    const organisationId = report.organisationId || '';
    const confirmed = confirm(
      t(
        'rescore-will-erase-the-old-data-and-perform-a-new-scoring-process-it-may-take-a-few-minutes-to-finish-are-you-sure',
      ) + '?',
    );
    if (confirmed && companyId && organisationId) {
      try {
        const body: TriggerScoringByNameDto = {
          isRescore: true,
        };
        // trigger company update
        const updated = await Score({
          id: companyId,
          body,
        });

        const { statusCode, errorCode } = updated;
        if (statusCode === 200 || statusCode === 202) {
          setNoticeData({
            message: t('rescoring-is-in-progress-it-might-finish-in-a-few-minutes'),
            severity: 'success',
          });
          const pollFn = async () => {
            return (await PollCompanyScore(companyId)).data;
          };
          const isFinished = (company: CompanyDetailedDto) => {
            return (shouldCancel && shouldCancel()) || company.scoringState === 'Completed';
          };

          // start polling every 5 seconds
          await pollAsync<CompanyDetailedDto>(pollFn, isFinished, 5);

          // refetch the lastest scored snapshot
          return await GetOrganisationScoredCompany(companyId, organisationId);
        } else {
          // trigger failed, rollback
          if (statusCode === 400 && errorCode === 'DissolvedCompany') {
            setNoticeData({
              message: t('scoring-cannot-be-done-as-company-current-state-is-dissolved'),
              severity: 'error',
            });
          }
        }
      } catch (e) {
        console.error(e);
        setNoticeData({
          message: t('cannot-rescore-the-company'),
          severity: 'error',
        });
      }
    }
    return report;
  };

  const handleDownloadReport = async (
    report: OrganisationScoredCompaniesDto,
    pdfStyle: PdfStyleDto,
    reportType: ReportTypeDto,
  ) => {
    const companyId = report.companyId || '';
    const organisationId = report.organisationId || '';
    try {
      const reportDate = report.scoredAt || companyId || '';
      const name = report.companyName || 'name unidentified';
      const filename = `${name} - Sustainability Report -${reportDate}.pdf`;
      // await savePdf(elementRef.current, filename);
      await downloadKosmosReport({
        companyId,
        organisationId,
        defaultFilename: filename,
        pdfStyle,
        reportType,
      });
      setNoticeData({
        message: t('a-report-should-be-downloaded'),
        severity: 'success',
      });
      return report;
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: t('cannot-download-the-report-for-this-company'),
        severity: 'error',
      });
    }
  };

  const columns: IColumn<OrganisationScoredCompaniesDto>[] = [
    {
      label: t('name'),
      render: (report) => {
        return report?.companyName || t('unknown');
      },
      sortIndex: 'name',
    },
    {
      label: t('country'),
      dataIndex: 'countryCode',
      render: (report) => {
        return report.companyCountry ?? '';
      },
    },
    {
      label: t('external-identifier'),
      render: (report) => {
        if (report.registrationNumbers && report.registrationNumbers.length) {
          return (
            <div>
              {report.registrationNumbers.map((item) => (
                <>
                  {item.typeDescription}: {item.identifier}
                  <br />
                </>
              ))}
            </div>
          );
        }
        return '';
      },
    },
    {
      label: t('esg-score'),
      render: (report) => {
        if (report && report.score && report.score > 0) {
          const companyId = report.companyId || '';
          return (
            <Link
              to={getKosmosReportLink(companyId, organisationId)}
              text={report.score.toString() || ''}
            />
          );
        } else {
          return <span>&nbsp;</span>;
        }
      },
    },
    {
      label: t('sg-score'),
      render: (report) => {
        if (report && report.sgScore && report.sgScore > 0) {
          const companyId = report.companyId || '';
          return (
            <Link
              to={getSgReportLink(companyId, organisationId)}
              text={report.sgScore.toString() || ''}
            />
          );
        } else {
          return <span>&nbsp;</span>;
        }
      },
    },
    {
      label: t('scoring-state'),
      dataIndex: 'scoringState',
    },
    {
      label: t('scored-at'),
      render: (record) => utc2str(record.scoredAt, 'YYYY-MM-DD HH:mm'),
      sortIndex: 'created',
    },
    {
      label: t('action'),
      actions: [
        {
          label: t('rescore'),
          hide: (report) => !report.companyId,
          onClick: handleRescoreCompany,
        },
        {
          label: t('esg-report'),
          hide: (report: OrganisationScoredCompaniesDto) =>
            !report.companyId || !CurrentUser.getInstance().isAdmin(),
          onClick: (report: OrganisationScoredCompaniesDto) =>
            handleDownloadReport(report, 'CoriolisEsg' as PdfStyleDto, 'Detailed' as ReportTypeDto),
        },
        {
          label: t('evo-summary-report'),
          hide: (report: OrganisationScoredCompaniesDto) =>
            !report.companyId || !CurrentUser.getInstance().isAdmin(),
          onClick: (report: OrganisationScoredCompaniesDto) =>
            handleDownloadReport(report, 'EvoEsg' as PdfStyleDto, 'Summary' as ReportTypeDto),
        },
        {
          label: t('evo-report'),
          hide: (report: OrganisationScoredCompaniesDto) =>
            !report.companyId || !CurrentUser.getInstance().isAdmin(),
          onClick: (report: OrganisationScoredCompaniesDto) =>
            handleDownloadReport(report, 'EvoEsg' as PdfStyleDto, 'Detailed' as ReportTypeDto),
        },
      ],
    },
  ];

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  React.useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const organisation = await GetCurrentOrganisation();
        if (!organisation) {
          setShowNotice(false);
        } else {
          setOrganisationId(organisation.id);
          const { remainingCredits = 0, isRestrictedByCredits } = organisation;
          if (isRestrictedByCredits && remainingCredits <= 0) {
            setShowNotice(true);
          }
        }
      } catch (e) {
        console.error(e);
      }
      setIsLoading(false);
    };
    loadData();
  }, []);

  return (
    <Root>
      <h2>{t('my-scored-companies')}</h2>
      {!isLoading && <OrganisationOutOfCreditNotice showNotice={showNotice} />}
      {organisationId ? (
        <ResourceTable
          filterSchema={filterSchema}
          columns={columns}
          onFetch={GetScoredCompanies}
          defaultQuery={[
            {
              column: 'lastScoredAtStart',
              value: '1970-01-01',
            },
            {
              column: 'organisationId',
              value: organisationId,
            },
          ]}
          defaultSort={{
            sortColumn: 'created',
            isDesc: true,
          }}
        />
      ) : null}

      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
